import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BookOpen, ChevronRight, Hash, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CollapsibleRecordRow, CollapsibleRecordList } from './CollapsibleRecordRow';
import { JournalEntryDetailModal } from './JournalEntryDetailModal';
import { useNavigate } from 'react-router-dom';

interface JournalEntryLogSectionProps {
  unaId?: string;
  maxVisible?: number;
}

const recordTypeColors: Record<string, string> = {
  'Private Contribution Received': 'bg-green-100 text-green-800',
  'Grant or Distribution Made': 'bg-blue-100 text-blue-800',
  'Expense Payment': 'bg-red-100 text-red-800',
  'Activity Log Entry': 'bg-purple-100 text-purple-800',
  'General Record': 'bg-gray-100 text-gray-800',
  'Member Appointment': 'bg-yellow-100 text-yellow-800',
  'Officer Appointment': 'bg-orange-100 text-orange-800',
  'Asset Contribution': 'bg-teal-100 text-teal-800',
  'Resolution Adopted': 'bg-indigo-100 text-indigo-800',
  'Meeting Conducted': 'bg-pink-100 text-pink-800',
};

const recordTypePrefixes: Record<string, string> = {
  'Private Contribution Received': 'RCP',
  'Grant or Distribution Made': 'DST',
  'Expense Payment': 'EXP',
  'Activity Log Entry': 'LOG',
  'General Record': 'JNL',
  'Member Appointment': 'MBR',
  'Officer Appointment': 'OFC',
  'Asset Contribution': 'AST',
  'Resolution Adopted': 'RSL',
  'Meeting Conducted': 'MTG',
};

export const JournalEntryLogSection: React.FC<JournalEntryLogSectionProps> = ({
  unaId,
  maxVisible = 3,
}) => {
  const navigate = useNavigate();
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [downloadingEntryId, setDownloadingEntryId] = useState<string | null>(null);

  const handleEntryDownload = async (entry: any) => {
    setDownloadingEntryId(entry.id);
    try {
      const { data: doc } = await supabase
        .from('generated_documents')
        .select('id, file_url, signed_file_url, file_path, document_name, custom_content, document_type, task_id')
        .eq('task_id', entry.workflow_task_id)
        .limit(1)
        .maybeSingle();

      let url: string | null = (doc?.signed_file_url as string) || (doc?.file_url as string) || null;
      let filePath: string | null = (doc?.file_path as string) || null;

      const canPrepare =
        !!doc?.id &&
        (!!doc?.custom_content ||
          (doc?.document_type === "formal_meeting_minutes" && !!doc?.task_id));
      if (!url && !filePath && canPrepare) {
        toast.info("This document just needs its PDF prepared. Preparing now…");
        const { regenerateDocumentPdf } = await import("@/lib/regeneratePdf");
        const res = await regenerateDocumentPdf(doc!.id as string);
        if (res.error) {
          toast.error(res.error);
          return;
        }
        if (res.file_path) filePath = res.file_path;
      }

      if (!url && !filePath) {
        toast.error("No document available for download");
        return;
      }
      const fallbackFilename = `${entry.record_title.replace(/[^a-z0-9]/gi, '-')}.pdf`;
      if (filePath) {
        const { data: signed } = await supabase.storage
          .from("documents")
          .createSignedUrl(filePath, 300, { download: fallbackFilename });
        url = signed?.signedUrl || url;
      }
      if (!url) {
        toast.error("Could not generate a download link");
        return;
      }
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fallbackFilename;
      link.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error("Failed to download document");
    } finally {
      setDownloadingEntryId(null);
    }
  };

  const { data: journalEntries, isLoading } = useQuery({
    queryKey: ['journal-entries', unaId],
    queryFn: async () => {
      if (!unaId) return [];

      const { data, error } = await supabase
        .from('una_journal_records' as any)
        .select('*')
        .eq('una_id', unaId)
        .order('record_date', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!unaId,
  });

  const { data: entryCount = 0 } = useQuery({
    queryKey: ['journal-entries-count', unaId],
    queryFn: async () => {
      if (!unaId) return 0;

      const { count, error } = await supabase
        .from('una_journal_records' as any)
        .select('*', { count: 'exact', head: true })
        .eq('una_id', unaId);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!unaId,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Journal Entry Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <CardTitle>Journal Entry Log</CardTitle>
              {entryCount > 0 && (
                <Badge variant="secondary">{entryCount}</Badge>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/workflows?filter=journal')}
            >
              New Entry
            </Button>
          </div>
          <CardDescription>
            Chronological record of all UNA transactions and activities
          </CardDescription>
        </CardHeader>
        <CardContent>
          {journalEntries && journalEntries.length > 0 ? (
            <CollapsibleRecordList maxVisible={maxVisible}>
              {journalEntries.map((entry: any) => {
                const typeColor = recordTypeColors[entry.record_type] || 'bg-gray-100 text-gray-800';
                const prefix = recordTypePrefixes[entry.record_type] || 'REC';
                
                return (
                  <CollapsibleRecordRow
                    key={entry.id}
                    id={entry.id}
                    title={entry.record_title}
                    date={entry.record_date}
                    type="journal"
                    badge={{
                      label: entry.record_type,
                      className: typeColor,
                    }}
                    subtitle={
                      entry.record_number 
                        ? `${entry.record_number}` 
                        : `${prefix}-${format(new Date(entry.record_date), 'yyyy')}`
                    }
                    onClick={() => setSelectedEntry(entry)}
                    actions={
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEntryDownload(entry);
                        }}
                        disabled={downloadingEntryId === entry.id}
                      >
                        {downloadingEntryId === entry.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    }
                    expandedContent={
                      <div className="space-y-2 text-sm">
                        {entry.record_number && (
                          <div className="flex items-center gap-2">
                            <Hash className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono">{entry.record_number}</span>
                          </div>
                        )}
                        {(entry.transaction_description || entry.activity_description) && (
                          <p className="text-muted-foreground">
                            {(entry.transaction_description || entry.activity_description)?.substring(0, 150)}
                            {(entry.transaction_description || entry.activity_description)?.length > 150 ? '...' : ''}
                          </p>
                        )}
                        {entry.authorized_by && (
                          <p className="text-xs">
                            <span className="text-muted-foreground">Authorized by:</span>{' '}
                            {entry.authorized_by}
                          </p>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEntry(entry);
                          }}
                        >
                          View Full Details
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    }
                  />
                );
              })}
            </CollapsibleRecordList>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No journal entries yet</p>
              <p className="text-sm mt-2">
                Use the "Record a Log or Journal Transaction" workflow to create your first entry
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <JournalEntryDetailModal
        entry={selectedEntry}
        open={!!selectedEntry}
        onOpenChange={(open) => !open && setSelectedEntry(null)}
      />
    </>
  );
};
